import { LANGUAGE_OPTIONS } from '../../../constants/main';
import { IMPORT_TRANSLATION_STATUS_TYPES, TRANSLATION_FILE_TYPES, TRANSLATION_REFERENCES_COLUMN, UTF_8_CHARSET } from '../../../constants/translations';
import decompressAndDecodeZipFiles from '../decompressAndDecodeZipFiles/ index';
import * as PapaParse from 'papaparse';
import { po } from 'gettext-parser';
import extractLanguageCodeFromLocale from '../extractLanguageCode';

export const REQUIRED_FIELD_MISSING_ERROR = 'requiredFieldIsMissingError';

// ------- File Reading and File Parsing section -------

/**
 * Asynchronously parses the uploaded file based on its extension.
 * Determines the file extension and selects the appropriate parsing strategy.
 *
 * @param {Object} params - The parameters object.
 * @param {File} params.file - The uploaded file object.
 * @param {string} params.primaryLanguage - The primary language code.
 * @returns {Promise<Object>} - The parsed data from the uploaded file.
 * @throws {Error} - If parsing fails.
 */
export const getUploadedFileParsedData = async ({ file, primaryLanguage }) => {
  const fileExtension = Object.values(TRANSLATION_FILE_TYPES)?.find(fileType => file.name.endsWith(fileType.extension))?.extension;
  const readAndParseFileStrategy = uploadedFileReadAndParseStrategy[fileExtension];

  if(readAndParseFileStrategy) {
    const parsedData = await readAndParseFileStrategy({ file, primaryLanguage });
    return parsedData;
  }
};

/**
 * Object containing strategies for reading and parsing different file types.
 * Each property corresponds to a file extension and maps to a specific parsing function.
 */
const uploadedFileReadAndParseStrategy = {
  [TRANSLATION_FILE_TYPES.CSV.extension]: (params) => readAndParseFile(params, readAndParseCSVFile),
  [TRANSLATION_FILE_TYPES.PO.extension]: (params) => readAndParseFile(params, readAndParsePoFile),
  [TRANSLATION_FILE_TYPES.ZIP.extension]: (params) => readAndParseFile(params, readAndParseZipFile)
};

/**
 * Generic function to read and parse a file using a provided parsing strategy.
 * Utilizes FileReader to read the file content and then applies the parsing strategy.
 *
 * @param {Object} params - The parameters object.
 * @param {File} params.file - The uploaded file object.
 * @param {string} params.primaryLanguage - The primary language code.
 * @param {Function} readStrategy - The function to parse the file content.
 * @returns {Promise<Object>} - The parsed data from the file.
 */
const readAndParseFile = ({ file, primaryLanguage }, readStrategy) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsedData = readStrategy(event.target.result, primaryLanguage);
        resolve(parsedData);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });

};

/**
 * Parses CSV file content.
 * Uses PapaParse to parse the CSV content and then transforms it.
 *
 * @param {ArrayBuffer} fileContent - The content of the uploaded file.
 * @param {string} primaryLanguage - The primary language code.
 * @returns {Promise<Object>} - The transformed CSV content.
 */
const readAndParseCSVFile = (fileContent, primaryLanguage) => {
  return new Promise((resolve, reject) => {
    const textDecoder = new TextDecoder(UTF_8_CHARSET);
    const csvContent = textDecoder.decode(fileContent);

    PapaParse.parse(csvContent, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {

        const requiredFields = [primaryLanguage, TRANSLATION_REFERENCES_COLUMN];
        const isRequiredFieldMissing = requiredFields.some(reqField => !result.meta.fields.includes(reqField));

        if(isRequiredFieldMissing) {
          reject(REQUIRED_FIELD_MISSING_ERROR);
        }

        const transformedCSVContent = transformCsvTranslationContent({
          data: result?.data,
          primaryLanguage,
          referenceColumn: TRANSLATION_REFERENCES_COLUMN,
          fields: result?.meta?.fields
        });

        resolve(transformedCSVContent);
      }
    });
  });

};

/**
 * Parses PO file content.
 * Decodes and parses the PO file content using gettext-parser.
 *
 * @param {ArrayBuffer} fileContent - The content of the uploaded file.
 * @returns {Promise<Object>} - The parsed and structured PO content.
 */
const readAndParsePoFile = (fileContent) => {
  return new Promise((resolve, reject) => {
    try {
      const textDecoder = new TextDecoder(UTF_8_CHARSET);
      const poContent = textDecoder.decode(fileContent);
      const parsedPoContent = parsePoContent(poContent);

      const { headers, translations } = parsedPoContent;
      const fileLanguage = extractLanguageCodeFromLocale(headers?.Language);

      const extractedContent = extractPoTranslationContent(translations);

      const groupedByLanguageData = {
        [fileLanguage]: {
          languageCode: fileLanguage,
          normalizedLanguageCode: extractLanguageKey(fileLanguage),
          data: extractedContent
        }
      };

      resolve(groupedByLanguageData);
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Parses ZIP file content.
 * Decodes and parses the ZIP file content, extracting PO files within the ZIP.
 *
 * @param {ArrayBuffer} fileContent - The content of the uploaded ZIP file.
 * @returns {Promise<Object>} - The parsed and structured content from PO files within the ZIP.
 */
const readAndParseZipFile = (fileContent) => {
  return new Promise((resolve, reject) => {
    try {

      // Decompress zip file to extract and use only po files
      const decodedZipContent = decompressAndDecodeZipFiles(fileContent, [TRANSLATION_FILE_TYPES.PO.extension]);
      const validPoFilesContent = Object.values(decodedZipContent?.success);

      let groupedByLanguageData = {};
      let languageCounters = {};

      // Iterate over the po files that were decomporessed
      if (validPoFilesContent?.length > 0) {

        validPoFilesContent.forEach(poFileContent => {

          // Parse each po file
          const parsedPoContent = parsePoContent(poFileContent);

          // Extract required values from the parsed content
          const { headers, translations } = parsedPoContent;
          let languageKey = extractLanguageCodeFromLocale(headers?.Language);

          const extractedContent = extractPoTranslationContent(translations);

          // ---- Handle duplicate languages ----

          // The incoming language might be a locale of style en_US, we need to extract the en language code for example
          const normalizedLanguageKey = extractLanguageKey(languageKey);

          // If we iterate over a language a first time then set the counter to 1
          // If we encounter the same language code for the second time then increased the counter for this language code
          if (normalizedLanguageKey in languageCounters) {
            languageCounters[normalizedLanguageKey] += 1;
          } else {
            languageCounters[normalizedLanguageKey] = 1;
          }

          // If the counter for this languageCode is larger than 1 it means it's a duplicate
          // To ensure proper handling later on we change the languageCode of each duplicated based on it's counter
          if (languageCounters[normalizedLanguageKey] > 1) {
            languageKey = `${languageKey}_${languageCounters[normalizedLanguageKey] - 1}`;
          }

          // Initialize for each language key it's data structure for grouping by language
          if (!groupedByLanguageData[languageKey]) {
            groupedByLanguageData[languageKey] = {
              languageCode: languageKey,
              normalizedLanguageCode: normalizedLanguageKey,
              data: {}
            };
          }

          // Iterate over the content of this language and assign it to the group by language structure
          Object.keys(extractedContent).forEach(entityKey => {
            if (!groupedByLanguageData[languageKey].data[entityKey]) {
              groupedByLanguageData[languageKey].data[entityKey] = [];
            }
            groupedByLanguageData[languageKey].data[entityKey] = extractedContent[entityKey];
          });
        });
      }

      resolve(groupedByLanguageData);
    } catch (err) {
      reject(err);
    }
  });
};

// ------- Generate warnings functions -------

/**
 * Generates language warning flags for the parsed data.
 * Checks for duplicate, invalid, primary, and enabled language conditions.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The parsed data grouped by language.
 * @param {string} params.primaryLanguage - The primary language code.
 * @param {Array<string>} params.enabledLanguages - The list of enabled language codes.
 * @returns {Array<Object>} - The validated language data with warning flags.
 */
export const generateLanguageWarningFlags = ({ data, primaryLanguage, enabledLanguages }) => {

  const seenKeys = new Set();
  const languageData = Object.entries(data).map(([key, value]) => (
    {
      languageCode: key,
      normalizedLanguageCode: extractLanguageKey(key),
      isDuplicate: false,
      isPrimary: false,
      isEnabled: false,
      isInvalid: false,
      isOverwritten: false,
      data: value?.data || {}
    }
  ));

  // Iterate over each language and based on the below conditions
  // change the value of the flags above
  const validatedLanguageData = languageData.map(item => {
    let languageItem = { ...item };

    // Check if the incoming language is part of the current enabled languages of the bot
    // Then set it to enable as default behavior
    if(enabledLanguages.includes(languageItem.normalizedLanguageCode) || languageItem.normalizedLanguageCode === primaryLanguage) {
      languageItem = { ...languageItem, isEnabled: true };
    }

    // Check if the incoming language is equal to the primary
    if(languageItem.normalizedLanguageCode === primaryLanguage) {
      languageItem = { ...languageItem, isPrimary: true, isEnabled: false };
    }

    // Check if the incoming language is being processed more than once
    // If true then it is duplicate
    if (seenKeys.has(languageItem.normalizedLanguageCode)) {
      languageItem = { ...languageItem, isDuplicate: true, isEnabled: false };
    } else {
      seenKeys.add(languageItem.normalizedLanguageCode);
    }

    // Check if the incoming language is included to the supported languages of the project
    if(!Array.from(LANGUAGE_OPTIONS)?.map(lang => lang.key)?.includes(languageItem.normalizedLanguageCode)) {
      languageItem = { ...languageItem, isInvalid: true, isEnabled: false };
    }

    return languageItem;
  });

  return validatedLanguageData;

};

// ------- PO Content transformation functions -------

/**
 * Parses PO file content using gettext-parser.
 *
 * @param {string} poContent - The PO file content as a string.
 * @returns {Object} - The parsed PO content.
 */
const parsePoContent = (poContent) => {
  return po.parse(poContent, { defaultCharset: UTF_8_CHARSET });
};

/**
 * Extracts translation content from parsed PO translations.
 *
 * @param {Object} translations - The translations object from parsed PO file.
 * @returns {Object} - The extracted translation content grouped by entity key.
 */
const extractPoTranslationContent = (translations) => {
  const groupDataByEntityKey = {};
  Object.values(translations['']).forEach(translation => {
    if (translation?.msgid?.length > 0 && translation?.msgstr[0]?.length > 0) {
      const entityReferences = translation?.comments?.reference?.split(',') || [];
      return entityReferences.forEach(ref => {
        const [entityKey, entityKeyValue] = ref.split(':');

        if(!groupDataByEntityKey[entityKey]) {
          groupDataByEntityKey[entityKey] = [];
        }

        const [entityId, nodeId] = entityKeyValue.split('/');

        groupDataByEntityKey[entityKey].push({
          entityKey,
          entityId,
          nodeId,
          originalContent: translation.msgid,
          translatedContent: translation.msgstr[0]
        });
      });
    }
  });

  return groupDataByEntityKey;

};

/**
 * Transforms CSV translation content to a structured format.
 *
 * @param {Object} params - The parameters object.
 * @param {Array<Object>} params.data - The parsed CSV data.
 * @param {string} params.primaryLanguage - The primary language code.
 * @param {string} params.referenceColumn - The column name for references.
 * @param {Array<string>} params.fields - The fields from the CSV file.
 * @returns {Object} - The transformed CSV content grouped by language.
 */
const transformCsvTranslationContent = ({
  data = [],
  primaryLanguage,
  referenceColumn,
  fields = []
}) => {
  const groupedByLanguageData = {};
  const languageFields = fields.filter(field => ![primaryLanguage, referenceColumn].includes(field));

  data.forEach(row => {
    const referenceColumnKey = row[referenceColumn];
    if(!referenceColumnKey){
      return;
    }
    const [entityKey, entityKeyValue] = referenceColumnKey.split(':');
    const [entityId, nodeId] = entityKeyValue.split('/');
    const primaryLanguageContent = row[primaryLanguage];

    languageFields.forEach(field => {
      if (row[field]?.length > 0) {
        if (!groupedByLanguageData[field]) {
          groupedByLanguageData[field] = {
            languageCode: field,
            normalizedLanguageCode: extractLanguageKey(field),
            data: {}
          };
        }

        if (!groupedByLanguageData[field].data[entityKey]) {
          groupedByLanguageData[field].data[entityKey] = [];
        }

        groupedByLanguageData[field].data[entityKey].push({
          entityKey,
          entityId,
          nodeId,
          originalContent: primaryLanguageContent,
          translatedContent: row[field]
        });
      }
    });
  });

  return groupedByLanguageData;
};

/**
 * Extracts the language key from a given string.
 * Removes any suffixes from the key.
 *
 * @param {string} key - The language key.
 * @returns {string} - The extracted language key.
 * @throws {Error} - If the key is not a string.
 */
const extractLanguageKey = (key) => {
  if(typeof key !== 'string') {
    throw new Error(`Param key should be type of string but is type of ${typeof key}.`);
  }
  return key.replace(/_.*/, '');
};

/**
 * Generates final translation warnings based on parsed data.
 * Analyzes translations and entities to determine content changes.
 *
 * @param {Object} params - The parameters object.
 * @param {Array<Object>} params.translationsData - The translations data with warning flags.
 * @param {Object} params.entitiesData - The entities data fetched from the server.
 * @param {string} params.primaryLanguage - The primary language code.
 * @param {Array<string>} params.enabledLanguages - The list of enabled language codes.
 * @returns {Object} - The final translation warnings and entity updates.
 */
export const generateFinalTranslationWarnings = ({ translationsData, entitiesData, primaryLanguage, enabledLanguages }) => {
  const finalData = [];
  let infoPerEntity = {};

  // Iterate over valid language codes to extract warnings based on content
  translationsData?.filter(item => !item.isInvalid)?.forEach(item => {
    let languageItem = { ...item };

    // Iterate over the data based on entity key
    Object.entries(languageItem?.data).forEach(([entityKey, data]) => {

      data?.forEach(translatedEntity => {

        // If the current entityId exists on the itemsMap then that means that the activity is not deleted
        if(entitiesData[entityKey]?.itemsMap?.has(translatedEntity?.entityId)) {

          // Deep clone the matching entity to ensure mutability because it is required
          let targetEntity = structuredClone(entitiesData[entityKey]?.itemsMap?.get(translatedEntity.entityId));

          // Group warnings by entity name
          if(!(targetEntity?.name[primaryLanguage] in infoPerEntity)) {
            infoPerEntity[targetEntity?.name[primaryLanguage]] = [];
          }

          const targetNodeIndex = targetEntity?.content?.nodes?.findIndex(node => node?.id === translatedEntity?.nodeId);

          if(targetNodeIndex !== -1) {
            if(!languageItem?.isPrimary) {
              const targetNode = targetEntity.content.nodes[targetNodeIndex];

              // If the original entity content is different than the current matching entity content then push the warning
              // that the original node has different content in primary language
              if(targetNode?.label[primaryLanguage]?.trim() !== translatedEntity?.originalContent?.trim()) {
                if(!infoPerEntity[targetEntity?.name[primaryLanguage]]?.find(item => item?.key === targetNode?.id)) {
                  infoPerEntity[targetEntity?.name[primaryLanguage]].push(
                    {
                      key: targetNode?.id,
                      status: IMPORT_TRANSLATION_STATUS_TYPES.CONTENT_CHANGED,
                      originalContent: translatedEntity?.originalContent?.trim(),
                      currentContent: targetNode?.label[primaryLanguage]?.trim(),
                      type: targetNode?.type
                    }
                  );
                }
              }

              // If there was content in the secondary language of the matching entity and it is different
              // than the incoming content then that means that we overwritte content.
              if(
                targetNode?.label[languageItem?.normalizedLanguageCode]?.length > 0
                && targetNode?.label[languageItem?.normalizedLanguageCode] !== translatedEntity.translatedContent
              ) {
                languageItem = { ...languageItem, isOverwritten: true };
              }
            }
          }
        }

      });
    });

    finalData.push({
      ...languageItem
    });
  });

  return {
    languageData: finalData,
    entityWarnings: infoPerEntity
  };
};

/**
 * Merges translated entities with existing entities data.
 * Combines translated content with existing content in the entities (flows, automated-answers, etc).
 *
 * @param {Array<Object>} data - The array of translated language data.
 * @param {Object} entitiesData - The existing entities data.
 * @returns {Object} - The merged translated entities data.
 */
export const mergeTranslatedEntities = (data, entitiesData) => {
  let finalTranslatedMap = new Map();

  // Iterate over each language item
  data.forEach(item => {

    // Extract the translated data as translated entities
    const { data: translatedEntities } = item;

    //Iterate over the translatedEntities which are grouped by flows, automated-answers etc
    Object.entries(translatedEntities).forEach(([key, value]) => {

      // If the finalTranslatedMap doesnt have the entity key (flow, automated-answers, etc)
      // then set it with an empty map as value, which is going to hold it's matching activities/entities
      if(!finalTranslatedMap.has(key)) {
        finalTranslatedMap.set(key, new Map());
      }

      // Iterate over each translation item which belongs to an entity key
      value.forEach(translationItem => {

        // Retrieve the matching entity map to set the corresponding values
        const entityMap = finalTranslatedMap.get(key);

        // Deep clone the target entity to ensure mutability because of the need to affect and mutate a high depth
        let targetEntity = structuredClone(entityMap.has(translationItem.entityId) ? entityMap.get(translationItem.entityId) : entitiesData[key]?.itemsMap?.get(translationItem.entityId));

        // If the flow is not deleted then set it to the matching entityMap
        if (targetEntity) {
          if (!entityMap.has(targetEntity.id)) {
            entityMap.set(targetEntity.id, targetEntity);
          }

          // Find the node that needs to be mutated
          const targetNodeIndex = targetEntity?.content?.nodes?.findIndex(node => node?.id === translationItem?.nodeId);

          // If it exists and is not deleted then directly mutate the label's matching languageCode
          if (targetNodeIndex !== -1) {
            targetEntity.content.nodes[targetNodeIndex].label[item.normalizedLanguageCode] = translationItem.translatedContent;
          }

          // Update both the entity map and then the finalTranslatedMap
          entityMap.set(targetEntity.id, targetEntity);
          finalTranslatedMap.set(key, entityMap);
        }
      });
    });
  });

  /* Convert the finalTranslatedMap to the structure of
    {
      [entityKey]: [...entities]
    }
  */
  const convertedMapDataToObject = {};
  for (let [key, map] of finalTranslatedMap.entries()) {
    convertedMapDataToObject[key] = Array.from(map.values());
  }

  return convertedMapDataToObject ;
};
